import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Send } from '@saleshandy/icons';
import { Button } from 'react-bootstrap';
import Select from '../../../../../../../../../shared/design-system/components/select';
import { ExtendedOption } from '../../../../../../../../../shared/design-system/components/transfer/transfer';
import { accessibleOnClick } from '../../../../../../../../../shared/utils/accessible-on-click';
import { RootState } from '../../../../../../../../../store/root-reducer';
import { Sequence } from '../../../../../../../../sequence/types/sequence';
import { SequenceStep } from '../../../../../../../../sequence/types/sequence-step';
import {
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../../../../../../shared/utils';
import { OverlayTooltip } from '../../../../../../../../../shared/design-system/components/overlay';
import TruncatedTextWithAutoTooltip from '../../../../../../../../../shared/components/truncated-text-with-auto-tooltip';

type Step = {
  id: number;
  number: number;
  sequence: {
    id: number;
    title: string;
    status: number;
    type: string;
  };
};

type ProspectSequence = {
  id: number;
  stepId: number;
  step: Step;
  sequenceProspect: any;
};

type IProps = {
  selectedProspectSequences: ProspectSequence[];
  allOutcomes: any[];
  isSingleProspectSubscribed: boolean;
  sendAddToStepRequest: (sequenceId: number, stepId: number) => void;
  sendGetSingleProspectSequencesDetailsRequest: () => void;
  selectedSequenceDefault?: Sequence;
  lockSelectedSequence?: boolean;
  onClearSelection?: () => void;
  onExpandedChange?: (expanded: boolean) => void;
  getSequenceListRequest?: () => void;
  getStepListRequest?: ({ sequenceId }) => void;
  initialIsExpanded?: boolean;
};

const AddProspectSequence: React.FC<IProps> = ({
  selectedProspectSequences,
  sendAddToStepRequest,
  sendGetSingleProspectSequencesDetailsRequest,
  selectedSequenceDefault,
  lockSelectedSequence,
  onClearSelection,
  onExpandedChange,
  getSequenceListRequest,
  getStepListRequest,
  initialIsExpanded,
}) => {
  const allSequences = useSelector(
    (state: RootState) => state?.prospect?.sequences,
  );

  const allSteps = useSelector((state: RootState) => state.prospect.steps);

  const getSequenceListRequestStatus = useSelector(
    (state: RootState) => state.prospect.getSequenceListRequest.status,
  );

  const getStepListRequestStatus = useSelector(
    (state: RootState) => state.prospect.getStepListRequest.status,
  );

  const addToStepRequestStatus = useSelector(
    (state: RootState) => state?.prospect?.addToStepRequest.status,
  );

  const [selectedSequence, setSelectedSequence] = useState<Sequence | null>(
    null,
  );
  const [selectedStep, setSelectedStep] = useState<Step | null>(null);
  const [isExpanded, setIsExpanded] = useState(initialIsExpanded);

  /**
   * 🔹 Fetch sequences ONLY when dropdown opens
   */
  const handleSequenceDropdownOpen = () => {
    if (
      !allSequences?.length &&
      !getIsRequestPending(getSequenceListRequestStatus)
    ) {
      getSequenceListRequest?.();
    }
  };

  /**
   * 🔹 If sequence is locked, ensure sequence list is fetched
   */
  useEffect(() => {
    if (
      lockSelectedSequence &&
      selectedSequenceDefault &&
      !allSequences.length &&
      !getIsRequestPending(getSequenceListRequestStatus)
    ) {
      getSequenceListRequest?.();
    }
  }, [
    lockSelectedSequence,
    selectedSequenceDefault,
    allSequences.length,
    getSequenceListRequestStatus,
  ]);

  // Fetch steps when a sequence is selected
  useEffect(() => {
    if (selectedSequence?.id) {
      getStepListRequest?.({ sequenceId: selectedSequence.id.toString() });
    }
  }, [selectedSequence]);

  // Monitor add to step request status
  useEffect(() => {
    executeOnRequestStatus({
      status: addToStepRequestStatus,
      onSuccess: () => {
        // sendGetSingleProspectSequencesDetailsRequest();
        setIsExpanded(false);
        setSelectedSequence(null);
        setSelectedStep(null);
      },
    });
  }, [addToStepRequestStatus, sendGetSingleProspectSequencesDetailsRequest]);

  useEffect(() => {
    if (selectedSequenceDefault) {
      setIsExpanded(true);
      setSelectedSequence(selectedSequenceDefault);
    }
  }, [selectedSequenceDefault, lockSelectedSequence]);

  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);

  const enrolledSequenceIds = new Set(
    selectedProspectSequences.map((seq) => seq.step.sequence.id),
  );

  const availableSequences = allSequences.filter(
    (seq) => !enrolledSequenceIds.has(seq.id),
  );

  const addedStepIds = selectedProspectSequences.map((seq) => seq.stepId);

  // Get steps for the selected sequence
  const sequenceSteps =
    allSteps?.reduce((acc: any[], step: SequenceStep, index: number) => {
      const prevDay = index === 0 ? 1 : acc[index - 1].day;
      const day = index === 0 ? 1 : prevDay + (step.relativeDays || 0);

      acc.push({
        ...step,
        key: step.id,
        sequence: selectedSequence,
        day,
      });

      return acc;
    }, []) || [];

  const handleCancel = () => {
    setIsExpanded(false);
    setSelectedSequence(null);
    setSelectedStep(null);
    if (selectedSequenceDefault && lockSelectedSequence) {
      onClearSelection();
    }
  };

  const handleAdd = () => {
    if (selectedSequence && selectedStep) {
      sendAddToStepRequest(selectedSequence.id, selectedStep.id);
    }
    if (selectedSequenceDefault && lockSelectedSequence) {
      onClearSelection();
    }
  };

  const handleExpand = () => {
    setIsExpanded(true);
  };

  return (
    <div className={`add-sequence-container ${isExpanded ? 'expanded' : ''}`}>
      <div className="add-sequence-header" {...accessibleOnClick(handleExpand)}>
        <Send width={20} height={20} className="gray-txt-15" />
        <span>Add to Sequence</span>
      </div>

      <div className="add-sequence-content">
        <div className="add-sequence-content-inner">
          <div className="add-sequence-body">
            <div className="add-sequence-field">
              <label className="add-sequence-label">Sequence</label>
              <Select
                id="select-sequence"
                aria-labelledby="select-sequence-label"
                options={availableSequences.map((seq) => ({
                  ...seq,
                  key: seq.id,
                }))}
                selectedOptionKey={selectedSequence?.id}
                optionRenderer={(option: ExtendedOption) => (
                  <TruncatedTextWithAutoTooltip text={(option as any).title} />
                )}
                isLoading={getIsRequestPending(getSequenceListRequestStatus)}
                selectedOptionRenderer={(options: ExtendedOption[]) => (
                  <TruncatedTextWithAutoTooltip
                    text={(options[0] as any)?.title}
                  />
                )}
                onToggle={(show: boolean) => {
                  if (show) handleSequenceDropdownOpen();
                }}
                onChange={(option: ExtendedOption[]) => {
                  const newSequence =
                    ((option[0] as unknown) as Sequence) ?? null;
                  setSelectedSequence(newSequence);
                  // Reset step selection when sequence changes
                  setSelectedStep(null);
                }}
                showSearch
                filterOption={(value, option) =>
                  (option as any).title
                    .toLowerCase()
                    .includes(value.toLowerCase())
                }
                placeholder="Select Sequence"
                disabled={lockSelectedSequence}
                className="select-sequence"
              />
            </div>
            <div className="add-sequence-field">
              <label className="add-sequence-label">Sequence steps</label>
              <Select
                options={sequenceSteps}
                selectedOptionKey={selectedStep?.id}
                optionRenderer={(option: ExtendedOption) => (
                  <OverlayTooltip
                    text="Prospect already exists in this step"
                    className={`${
                      addedStepIds.includes(Number(option.key))
                        ? ''
                        : 'sequence-disabled-tooltip'
                    }`}
                  >
                    <span
                      className={`option-content ${
                        addedStepIds.includes(Number(option.key))
                          ? 'disabled'
                          : ''
                      }`}
                    >
                      Step {(option as any).number} - Day {(option as any).day}
                    </span>
                  </OverlayTooltip>
                )}
                selectedOptionRenderer={(options: ExtendedOption[]) => (
                  <span>
                    Step {(options[0] as any)?.number} - Day{' '}
                    {(options[0] as any)?.day}
                  </span>
                )}
                onChange={(option: ExtendedOption[]) => {
                  if (addedStepIds.includes(Number(option[0]?.key))) {
                    return;
                  }
                  setSelectedStep((option[0] as any) ?? null);
                }}
                showSearch
                filterOption={(value, option) =>
                  (option as any).number.toString().includes(value)
                }
                placeholder="Select Step of Sequence"
                className="select-sequence-step"
                disabled={!selectedSequence}
                isLoading={getIsRequestPending(getStepListRequestStatus)}
              />
            </div>
          </div>

          <div className="add-sequence-footer">
            <div className="cancel-btn" {...accessibleOnClick(handleCancel)}>
              Cancel
            </div>
            <div className="add-btn">
              <Button
                {...accessibleOnClick(handleAdd)}
                disabled={
                  !selectedSequence ||
                  !selectedStep ||
                  getIsRequestPending(addToStepRequestStatus)
                }
              >
                {getIsRequestPending(addToStepRequestStatus)
                  ? 'Adding...'
                  : 'Add'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddProspectSequence;
